import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { HistoryObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type HistoryStoreAction =
  | { type: "SET_HISTORY"; payload: HistoryObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_HISTORY"; payload: HistoryObj }
  | { type: "DELETE_HISTORY"; payload: string }
  | { type: "SET_HISTORY_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_HISTORY_SAVING_NEW"; payload: boolean };

// Reducer Function
const historyStoreReducer = (
  state: HistoryStoreState,
  action: HistoryStoreAction
): HistoryStoreState => {
  switch (action.type) {
    case "SET_HISTORY":
      return {
        ...state,
        history: action.payload,
        historyLoading: false,
      };

    case "SET_LOADING":
      return { ...state, historyLoading: action.payload };

    case "ADD_OR_UPDATE_HISTORY":
      const index = state.history.findIndex((l) => l.id === action.payload.id);
      let updatedHistory = [...state.history];
      if (index > -1) {
        updatedHistory[index] = action.payload;
      } else {
        updatedHistory.push(action.payload);
      }
      return { ...state, history: updatedHistory };

    case "DELETE_HISTORY":
      return {
        ...state,
        history: state.history.filter((l) => l.id !== action.payload),
      };
    case "SET_HISTORY_FILES_TO_UPLOAD":
      return { ...state, historyFilestoUpload: action.payload };
    case "SET_HISTORY_SAVING_NEW":
      return { ...state, historySavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface HistoryStoreState {
  history: HistoryObj[];
  historyLoading: boolean;
  historySavingNew: boolean;
  historyFilestoUpload: File[];
}

// Hook
export const useHistoryStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(historyStoreReducer, {
    history: [],
    historyLoading: true,
    historyFilestoUpload: [],
    historySavingNew: false,
  });

  const deleteHistoryById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchHistoryData = useV3GenericGetData<HistoryObj[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createHistory = useV3Create<HistoryObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateHistory = useV3Update<HistoryObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<HistoryObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleHistoryFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.historyFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "history",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_HISTORY",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_HISTORY_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.historyFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadHistoryDocument = useCallback(
    async (history_id: string, document_id: string | null) => {
      if (!document_id || !history_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading file...");

      const params: any = {
        document_id: document_id,
        entity: "history",
        entity_id: history_id,
        ud_id: ud_id,
      };

      downloadDocument(params)
        .then((res: any) => {
          if (res) {
            // download File from s3 res and get file name from mathcing document id in history objects' documents array
            downloadFileFromS3(
              res,
              state.history
                ?.find((p) => p.id === history_id)
                ?.documents?.find((d) => d.document_id === document_id)
                ?.document_name || "Snug_Download.error"
            );
          } else {
            reportError(new Error(`Document Error: Failed to get document with message "No response"`));
          }
        })
        .catch((err) => {
          toast.error("Failed to download file");
          reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
        });
    },
    [downloadDocument, state.history, ud_id]
  );

  const handleDeleteHistoryDocument = useCallback(
    async (history_id: string, document_id: string | null): Promise<void> => {
      if (!document_id || !history_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_HISTORY_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "history",
          entity_id: history_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the history object
          const updatedHistory = state.history.map((p) => {
            if (p.id === history_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_HISTORY",
            payload: updatedHistory,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_HISTORY_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.history, deleteDocumentById, ud_id]
  );

  const handleHistoryFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_HISTORY_FILES_TO_UPLOAD",
        payload: [...state.historyFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.historyFilestoUpload]
  );

  const handleHistoryFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.historyFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_HISTORY_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.historyFilestoUpload]
  );

  const handleHistoryFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_HISTORY_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchHistoryItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchHistoryData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "history",
        });

        if (isMounted()) {
          dispatch({ type: "SET_HISTORY", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchHistoryData, isMounted]
  );

  const handleAddOrUpdateHistorySingleCall = useCallback(
    async (historyItem: HistoryObj): Promise<HistoryObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(historyItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateHistory(
            {
              ud_id: ud_id,
              entity: "history",
              entity_id: historyItem.id || undefined,
            },
            historyItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createHistory(
            { ud_id: ud_id, entity: "history" }, // Adjust according to your actual API parameter requirements
            historyItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_HISTORY", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save history item");
        console.error(error);
        return null;
      }
    },
    [ud_id, createHistory, updateHistory]
  );

  const handleAddOrUpdateHistory = useCallback(
    async (historyItem: HistoryObj) => {
      dispatch({ type: "SET_HISTORY_SAVING_NEW", payload: true });
      if (state.historyFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateHistorySingleCall(historyItem);
          if (res) {
            await handleHistoryFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error("Failed to add or edit history data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_HISTORY_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateHistorySingleCall(historyItem);
        } catch (error) {
          toast.error("Failed to add or edit history data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_HISTORY_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateHistorySingleCall,
      state.historyFilestoUpload,
      handleHistoryFilesUpload,
    ]
  );

  const handleDeleteHistory = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_HISTORY_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteHistoryById({
          ud_id: ud_id,
          entity: "history",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_HISTORY", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_HISTORY_SAVING_NEW", payload: false });
      }
    },
    [deleteHistoryById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchHistoryItems(ud_id); // Fetch all history items
    }
  }, [ fetchHistoryItems, ud_id]);

  return {
    history: state.history,
    historyLoading: state.historyLoading,
    historySavingNew: state.historySavingNew,
    historyFilestoUpload: state.historyFilestoUpload,
    handleAddOrUpdateHistory,
    handleDeleteHistory,
    handleHistoryFileRemove,
    handleHistoryFilesDrop,
    handleHistoryFilesRemoveAll,
    handleDownloadHistoryDocument,
    handleDeleteHistoryDocument,
    handleHistoryFilesUpload,
  };
};
